// ./src/components/features/Inventory/InventoryBarcodesEditor/InventoryBarcodesEditor.tsx
//
// Repeatable editor for an item's scannable barcodes (code + optional label). The
// full desired set is sent on save and diffed on `id` server-side, so existing rows
// keep their id. Column labels live in a separate header row (not per-first-row) so
// the trash action stays aligned across rows — see the em-input-row-icon-alignment note.
//
// ⚠ ON A PHONE each row is a small card (ROADMAP #65 f, owner 2026-09-28). The desktop grid gave the
// units field 2/12 of a ~343px bottom sheet — about ONE visible digit beside its own stepper — and a
// phone is exactly where this field is filled in, standing at a pallet with a scanned case in hand.

import React, { useCallback, useEffect, useState } from "react";
import { Stack, Group, Grid, TextInput, NumberInput, ActionIcon, Tooltip, Button, Text, Box, Paper } from "@mantine/core";
import { StackedModal } from "@em/ui";
import { useTranslation } from "react-i18next";

import { InventoryBarcode } from "@/utils/api";
import { AppIcon, QrScanner } from "@/components/generic";
import { useAppEnvironment } from "@/contexts";
import { parseUnitsPerCode } from "@/utils/inventoryScanLine";

export interface InventoryBarcodesEditorProps {
  value: InventoryBarcode[];
  onChange: (barcodes: InventoryBarcode[]) => void;
  disabled?: boolean;
  error?: string | null;
}

const emptyBarcode = (): InventoryBarcode => ({ code: "", label: "", unitsPerCode: 1 });

/** The pack sizes a phone offers as one tap — the common case shapes. */
const UNITS_PRESETS = [1, 6, 12, 24];

interface UnitsPerCodeInputProps {
  value: number;
  onChange: (units: number) => void;
  disabled?: boolean;
  label?: string;
  compactControls?: boolean;
}

/**
 * Units-per-pack, editable the way a person actually types.
 *
 * ⚠⚠ THE FIELD USED TO SNAP BACK TO 1 ON EVERY KEYSTROKE (#65 f). Its change handler coerced an
 * empty value to 1, so clearing it to type "24" left "1" — then "12", then "124". The DRAFT is kept
 * here as typed; only a real value (a whole number ≥ 1) reaches the row, so the form never holds an
 * invalid one, and leaving the field while it is empty puts the last good value back.
 */
const UnitsPerCodeInput: React.FC<UnitsPerCodeInputProps> = ({ value, onChange, disabled, label, compactControls }) => {
  const [draft, setDraft] = useState<number | string>(value);

  // A value set from outside (a preset tap, a reload, the row being seeded) replaces the draft —
  // unless the draft already says the same thing, which is the case on every keystroke.
  useEffect(() => {
    setDraft((current) => (parseUnitsPerCode(current) === value ? current : value));
  }, [value]);

  return (
    <NumberInput
      label={label}
      value={draft}
      onChange={(next) => {
        setDraft(next);

        const units = parseUnitsPerCode(next);

        if (units !== null && units !== value) {
          onChange(units);
        }
      }}
      onBlur={() => {
        if (parseUnitsPerCode(draft) === null) {
          setDraft(value);
        }
      }}
      disabled={disabled}
      min={1}
      // ⚠ Mantine's own clamp-on-blur would race the reset above: typing "0" then leaving clamps
      // the ROW to 1 while this puts the DRAFT back to the old value. One owner for the rule.
      clampBehavior="none"
      allowNegative={false}
      allowDecimal={false}
      hideControls={compactControls}
      inputMode="numeric"
      size="md"
    />
  );
};

export const InventoryBarcodesEditor: React.FC<InventoryBarcodesEditorProps> = ({
  value,
  onChange,
  disabled = false,
  error,
}) => {
  const { t } = useTranslation(["inventory", "common"]);
  const { isMobile } = useAppEnvironment();

  const codeLabel = t("inventory:barcodes.columns.code", { defaultValue: "Barcode / code" });
  const labelLabel = t("inventory:barcodes.columns.label", { defaultValue: "Label (optional)" });
  const unitsLabel = t("inventory:barcodes.columns.unitsPerCode", { defaultValue: "Units / pack" });
  const codePlaceholder = t("inventory:barcodes.fields.code.placeholder", { defaultValue: "e.g. 0123456789012" });
  const labelPlaceholder = t("inventory:barcodes.fields.label.placeholder", { defaultValue: "e.g. Case of 24" });
  const removeLabel = t("common:buttons.remove", { defaultValue: "Remove" });

  const updateAt = useCallback(
    (index: number, patch: Partial<InventoryBarcode>) => {
      onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    },
    [value, onChange]
  );

  const removeAt = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange]
  );

  const addBarcode = useCallback(() => {
    onChange([...value, emptyBarcode()]);
  }, [value, onChange]);

  // ── Scan-to-fill a barcode row ──
  const [scanOpen, setScanOpen] = useState(false);

  const handleScanned = useCallback(
    (text: string) => {
      const code = (text || "").trim();

      if (!code) {
        return;
      }

      // Fill the first row whose code is empty, else append a new row.
      const emptyIdx = value.findIndex((b) => !(b.code || "").trim());

      if (emptyIdx >= 0) {
        onChange(value.map((b, i) => (i === emptyIdx ? { ...b, code } : b)));
      } else {
        onChange([...value, { ...emptyBarcode(), code }]);
      }

      setScanOpen(false);
    },
    [value, onChange]
  );

  return (
    <Stack gap="xs">
      {/* PHONE: one card per barcode, every field full-width or half-width with its own label. */}
      {isMobile &&
        value.map((row, index) => (
          <Paper key={row.id ?? index} withBorder radius="md" p="sm">
            <Stack gap="xs">
              <Group gap="xs" wrap="nowrap" align="flex-end">
                <TextInput
                  label={codeLabel}
                  placeholder={codePlaceholder}
                  value={row.code ?? ""}
                  onChange={(e) => updateAt(index, { code: e.currentTarget.value })}
                  disabled={disabled}
                  leftSection={<AppIcon iconName="Qrcode" size={16} />}
                  size="md"
                  style={{ flex: 1, minWidth: 0 }}
                />
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="input-md"
                  onClick={() => removeAt(index)}
                  disabled={disabled}
                  aria-label={removeLabel}
                >
                  <AppIcon iconName="Trash" size={18} />
                </ActionIcon>
              </Group>

              <Group gap="xs" wrap="nowrap" align="flex-start">
                <TextInput
                  label={labelLabel}
                  placeholder={labelPlaceholder}
                  value={row.label ?? ""}
                  onChange={(e) => updateAt(index, { label: e.currentTarget.value })}
                  disabled={disabled}
                  size="md"
                  style={{ flex: 1, minWidth: 0 }}
                />
                <Box w={120}>
                  <UnitsPerCodeInput
                    label={unitsLabel}
                    value={row.unitsPerCode ?? 1}
                    onChange={(units) => updateAt(index, { unitsPerCode: units })}
                    disabled={disabled}
                    compactControls
                  />
                </Box>
              </Group>

              {/* One tap for the usual pack sizes — faster than any keyboard on a phone. */}
              <Group gap="xs">
                {UNITS_PRESETS.map((units) => (
                  <Button
                    key={units}
                    size="md"
                    px="md"
                    variant={(row.unitsPerCode ?? 1) === units ? "filled" : "light"}
                    onClick={() => updateAt(index, { unitsPerCode: units })}
                    disabled={disabled}
                  >
                    {units}
                  </Button>
                ))}
              </Group>
            </Stack>
          </Paper>
        ))}

      {/* WIDER SCREENS: the aligned grid, with the column labels in their own header row. */}
      {!isMobile && value.length > 0 && (
        <Grid gutter="xs" align="center" px={2}>
          <Grid.Col span={4}>
            <Text size="sm" c="dimmed" fw={600}>
              {codeLabel}
            </Text>
          </Grid.Col>
          <Grid.Col span={4}>
            <Text size="sm" c="dimmed" fw={600}>
              {labelLabel}
            </Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size="sm" c="dimmed" fw={600}>
              {unitsLabel}
            </Text>
          </Grid.Col>
          <Grid.Col span={1} />
        </Grid>
      )}

      {!isMobile &&
        value.map((row, index) => (
          <Grid key={row.id ?? index} gutter="xs" align="center">
            <Grid.Col span={4}>
              <TextInput
                placeholder={codePlaceholder}
                value={row.code ?? ""}
                onChange={(e) => updateAt(index, { code: e.currentTarget.value })}
                disabled={disabled}
                leftSection={<AppIcon iconName="Qrcode" size={16} />}
                size="md"
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <TextInput
                placeholder={labelPlaceholder}
                value={row.label ?? ""}
                onChange={(e) => updateAt(index, { label: e.currentTarget.value })}
                disabled={disabled}
                size="md"
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <UnitsPerCodeInput
                value={row.unitsPerCode ?? 1}
                onChange={(units) => updateAt(index, { unitsPerCode: units })}
                disabled={disabled}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Group gap={4} wrap="nowrap" justify="flex-end">
                <Tooltip label={removeLabel} withArrow position="top">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={() => removeAt(index)}
                    disabled={disabled}
                    aria-label={removeLabel}
                  >
                    <AppIcon iconName="Trash" size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Grid.Col>
          </Grid>
        ))}

      {value.length === 0 && (
        <Box>
          <Text size="md" c="dimmed">
            {t("inventory:barcodes.empty", {
              defaultValue: "No barcodes yet — add one to scan this item at a station.",
            })}
          </Text>
        </Box>
      )}

      {error && (
        <Text size="md" c="red">
          {error}
        </Text>
      )}

      <Group>
        <Button
          variant="light"
          size="md"
          onClick={addBarcode}
          disabled={disabled}
          leftSection={<AppIcon iconName="Plus" size={14} />}
        >
          {t("inventory:barcodes.add", { defaultValue: "Add barcode" })}
        </Button>
        <Button
          variant="subtle"
          size="md"
          onClick={() => setScanOpen(true)}
          disabled={disabled}
          leftSection={<AppIcon iconName="Scan" size={16} />}
        >
          {t("inventory:barcodes.scan", { defaultValue: "Scan" })}
        </Button>
      </Group>

      <StackedModal
        opened={scanOpen}
        onClose={() => setScanOpen(false)}
        title={t("inventory:barcodes.scanTitle", { defaultValue: "Scan a barcode" })}
        centered
        size="sm"
      >
        <Stack gap="sm">
          <QrScanner active={scanOpen} guide="wide" decodeArea="guide" onDecode={handleScanned} height={260} />
          <Text size="md" c="dimmed" ta="center">
            {t("inventory:barcodes.scanHint", { defaultValue: "Point the camera at the barcode." })}
          </Text>
        </Stack>
      </StackedModal>
    </Stack>
  );
};

export default InventoryBarcodesEditor;
